import type { ShortTaskOutput } from "@orsane/schema/tasks";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTRPC } from "@/lib/trpc";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";

function arraysEqual(a: ShortTaskOutput[] | null, b: ShortTaskOutput[] | null): boolean {
    if (a === b) return true;
    if (!a || !b) return a === b;
    if (a.length !== b.length) return false;
    const aIds = a
        .map((t) => t.id)
        .sort()
        .join(",");
    const bIds = b
        .map((t) => t.id)
        .sort()
        .join(",");
    return aIds === bIds;
}

export function TaskFilterPopover({
    onChange,
    value,
    showEmpty = true,
}: {
    onChange: (value: ShortTaskOutput[], closePopup: boolean) => void;
    value: ShortTaskOutput[] | null;
    showEmpty?: boolean;
}) {
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(timer);
    }, [search]);

    const trpc = useTRPC();
    const { data } = useQuery(
        trpc.task.search.queryOptions({ search: debouncedSearch || undefined, limit: 20 }, { staleTime: 0 })
    );

    const prevValueRef = useRef<ShortTaskOutput[] | null>(null);

    const stableValue = useMemo(() => {
        if (arraysEqual(value, prevValueRef.current)) {
            return prevValueRef.current;
        }
        prevValueRef.current = value;
        return value;
    }, [value]);

    const { selectedTasks, unselectedTasks } = useMemo(() => {
        const selected = stableValue || [];
        const unselected = data?.filter((t) => !selected.some((v) => v.id === t.id)) || [];
        return { selectedTasks: selected, unselectedTasks: unselected };
    }, [stableValue, data]);

    const handleSelect = useCallback(
        (task: ShortTaskOutput) => {
            const currentSelected = stableValue || [];
            if (currentSelected.some((t) => t.id === task.id)) {
                const newTasks = currentSelected.filter((t) => t.id !== task.id);
                onChange(newTasks, false);
            } else {
                onChange([...currentSelected, task], false);
            }
        },
        [stableValue, onChange]
    );

    return (
        <>
            <div className="p-3 flex gap-1 flex-wrap overflow-hidden border-b">
                {!stableValue?.length && (
                    <div className="leading-none grid place-items-center pl-1 text-zinc-400 py-1">
                        No tasks selected
                    </div>
                )}
                {selectedTasks.map((task) => (
                    <div
                        className="flex items-center py-0 pl-1 text-sm gap-1 font-normal rounded-sm bg-zinc-200/50"
                        key={`selected_items_${task.id}`}
                    >
                        <span className="text-foreground-muted text-xs">T-{task.number}</span>
                        <div className="text-nowrap py-0.5 ml-1">{task.title}</div>
                        <button
                            type="button"
                            className="block pr-1 rounded-r-sm h-full px-0.5 appearance-none hover:text-orsane-primary hover:bg-black/10"
                            onClick={() => handleSelect(task)}
                        >
                            <X className="size-3" />
                        </button>
                    </div>
                ))}
            </div>
            <Command shouldFilter={false}>
                <CommandInput autoFocus placeholder="Search for a task..." value={search} onValueChange={setSearch} />
                <CommandList>
                    <CommandGroup>
                        {unselectedTasks.map((task) => (
                            <CommandItem
                                value={task.id.toString()}
                                key={task.id}
                                onClick={(e) => e.stopPropagation()}
                                onSelect={() => handleSelect(task)}
                            >
                                <span className="text-foreground-muted shrink-0">T-{task.number}</span>
                                <span className="truncate">{task.title}</span>
                            </CommandItem>
                        ))}
                    </CommandGroup>
                    <CommandEmpty>No tasks found.</CommandEmpty>
                </CommandList>
            </Command>
        </>
    );
}
